import LandingFooter, { NestedLandingFooterContext } from "./LandingFooter";
import React, { useEffect, useState } from 'react';
import { ArrowRight, BarChart3, Bot, Menu, ShieldCheck, Target, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import './BespokeLandingReleaseShell.css';
import TrafficLandingV2 from './TrafficLandingV2';
import type { LandingPageConfig, TemplateTranslations } from './TrafficLandingTemplate';
import { useLandingLocalizationArtifact } from '../../utils/landingLocalization';

interface ShellCopy {
  skip: string;
  pricing: string;
  resources: string;
  company: string;
  login: string;
  start: string;
  product: string;
  legal: string;
}

const ENGLISH_COPY: ShellCopy = {
  skip: 'Skip to main content', pricing: 'Pricing', resources: 'Resources', company: 'Company',
  login: 'Log in', start: 'Start free', product: 'Product', legal: 'Legal',
};

const SHELL_COPY: Record<string, ShellCopy> = {
  ar: { skip: 'انتقل إلى المحتوى', pricing: 'الأسعار', resources: 'الموارد', company: 'الشركة', login: 'تسجيل الدخول', start: 'ابدأ مجانًا', product: 'المنتج', legal: 'قانوني' },
  bn: { skip: 'মূল বিষয়বস্তুতে যান', pricing: 'মূল্য', resources: 'রিসোর্স', company: 'কোম্পানি', login: 'লগ ইন', start: 'বিনামূল্যে শুরু করুন', product: 'পণ্য', legal: 'আইনি' },
  cs: { skip: 'Přejít na hlavní obsah', pricing: 'Ceny', resources: 'Zdroje', company: 'Společnost', login: 'Přihlásit se', start: 'Začít zdarma', product: 'Produkt', legal: 'Právní' },
  de: { skip: 'Zum Hauptinhalt springen', pricing: 'Preise', resources: 'Ressourcen', company: 'Unternehmen', login: 'Anmelden', start: 'Kostenlos starten', product: 'Produkt', legal: 'Rechtliches' },
  es: { skip: 'Ir al contenido principal', pricing: 'Precios', resources: 'Recursos', company: 'Empresa', login: 'Iniciar sesión', start: 'Empezar gratis', product: 'Producto', legal: 'Legal' },
  fr: { skip: 'Aller au contenu principal', pricing: 'Tarifs', resources: 'Ressources', company: 'Entreprise', login: 'Connexion', start: 'Commencer gratuitement', product: 'Produit', legal: 'Mentions légales' },
  he: { skip: 'דלג לתוכן הראשי', pricing: 'מחירים', resources: 'משאבים', company: 'חברה', login: 'התחברות', start: 'התחל בחינם', product: 'מוצר', legal: 'משפטי' },
  hi: { skip: 'मुख्य सामग्री पर जाएँ', pricing: 'मूल्य', resources: 'संसाधन', company: 'कंपनी', login: 'लॉग इन', start: 'मुफ़्त शुरू करें', product: 'उत्पाद', legal: 'कानूनी' },
  id: { skip: 'Lewati ke konten utama', pricing: 'Harga', resources: 'Sumber daya', company: 'Perusahaan', login: 'Masuk', start: 'Mulai gratis', product: 'Produk', legal: 'Legal' },
  it: { skip: 'Vai al contenuto principale', pricing: 'Prezzi', resources: 'Risorse', company: 'Azienda', login: 'Accedi', start: 'Inizia gratis', product: 'Prodotto', legal: 'Note legali' },
  ja: { skip: 'メインコンテンツへ移動', pricing: '料金', resources: 'リソース', company: '会社', login: 'ログイン', start: '無料で始める', product: '製品', legal: '法的情報' },
  ko: { skip: '본문으로 이동', pricing: '가격', resources: '자료', company: '회사', login: '로그인', start: '무료로 시작', product: '제품', legal: '법률 정보' },
  nl: { skip: 'Naar hoofdinhoud', pricing: 'Prijzen', resources: 'Bronnen', company: 'Bedrijf', login: 'Inloggen', start: 'Gratis starten', product: 'Product', legal: 'Juridisch' },
  pl: { skip: 'Przejdź do treści głównej', pricing: 'Cennik', resources: 'Materiały', company: 'Firma', login: 'Zaloguj się', start: 'Zacznij bezpłatnie', product: 'Produkt', legal: 'Informacje prawne' },
  pt: { skip: 'Ir para o conteúdo principal', pricing: 'Preços', resources: 'Recursos', company: 'Empresa', login: 'Entrar', start: 'Começar grátis', product: 'Produto', legal: 'Legal' },
  ro: { skip: 'Sari la conținutul principal', pricing: 'Prețuri', resources: 'Resurse', company: 'Companie', login: 'Autentificare', start: 'Începe gratuit', product: 'Produs', legal: 'Legal' },
  ru: { skip: 'Перейти к основному содержанию', pricing: 'Цены', resources: 'Ресурсы', company: 'Компания', login: 'Войти', start: 'Начать бесплатно', product: 'Продукт', legal: 'Правовая информация' },
  sv: { skip: 'Gå till huvudinnehållet', pricing: 'Priser', resources: 'Resurser', company: 'Företag', login: 'Logga in', start: 'Börja gratis', product: 'Produkt', legal: 'Juridiskt' },
  th: { skip: 'ข้ามไปยังเนื้อหาหลัก', pricing: 'ราคา', resources: 'แหล่งข้อมูล', company: 'บริษัท', login: 'เข้าสู่ระบบ', start: 'เริ่มฟรี', product: 'ผลิตภัณฑ์', legal: 'กฎหมาย' },
  tl: { skip: 'Pumunta sa pangunahing nilalaman', pricing: 'Presyo', resources: 'Mga mapagkukunan', company: 'Kumpanya', login: 'Mag-log in', start: 'Magsimula nang libre', product: 'Produkto', legal: 'Legal' },
  tr: { skip: 'Ana içeriğe geç', pricing: 'Fiyatlar', resources: 'Kaynaklar', company: 'Şirket', login: 'Giriş yap', start: 'Ücretsiz başla', product: 'Ürün', legal: 'Yasal' },
  vi: { skip: 'Chuyển đến nội dung chính', pricing: 'Bảng giá', resources: 'Tài nguyên', company: 'Công ty', login: 'Đăng nhập', start: 'Bắt đầu miễn phí', product: 'Sản phẩm', legal: 'Pháp lý' },
  zh: { skip: '跳到主要内容', pricing: '价格', resources: '资源', company: '公司', login: '登录', start: '免费开始', product: '产品', legal: '法律信息' },
};

const RTL_LOCALES = new Set(['ar', 'he']);

export const BESPOKE_LANDING_PATHS = new Set([
  '/bn/organic-website-traffic',
  '/bn/website-traffic-kinun',
  '/buy-website-traffic',
  '/cs/organic-website-traffic',
  '/de/organischen-traffic-kaufen',
  '/es/comprar-trafico-organico',
  '/fr/acheter-trafic-organique',
  '/free-website-traffic',
  '/he/organic-website-traffic',
  '/hi/organic-website-traffic',
  '/hi/website-traffic-kharidein',
  '/id/beli-traffic-organik',
  '/it/comprare-traffico-organico',
  '/ja/buy-organic-traffic',
  '/ko/buy-organic-traffic',
  '/modern-traffic',
  '/organic-website-traffic',
  '/pt/comprar-trafego-organico',
  '/ro/organic-website-traffic',
  '/ru/kupit-organicheskiy-trafik',
  '/sv/organic-website-traffic',
  '/tl/organic-website-traffic',
  '/tr/organik-hit-satin-al',
  '/zh/goumai-ziran-liuliang',
]);

export const isBespokeLandingPath = (pathname: string): boolean => (
  BESPOKE_LANDING_PATHS.has(pathname === '/' ? '/' : pathname.replace(/\/+$/, ''))
);

interface BespokeLandingReleaseShellProps {
  children: React.ReactNode;
}

const BespokeLandingReleaseShell: React.FC<BespokeLandingReleaseShellProps> = ({ children }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { artifact, loading } = useLandingLocalizationArtifact(location.pathname);
  const localeCandidate = location.pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? 'en';
  const locale = SHELL_COPY[localeCandidate] ? localeCandidate : 'en';
  const copy = SHELL_COPY[locale] ?? ENGLISH_COPY;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (loading) return <div data-landing-localization="loading" aria-busy="true" />;

  if (artifact) {
    const localized = artifact.content as Partial<LandingPageConfig>;
    const localizedFeatures = (localized.features ?? []).map((feature, index) => ({
      ...feature,
      icon: index === 0 ? <Target /> : index === 1 ? <BarChart3 /> : <ShieldCheck />,
    }));
    const config: LandingPageConfig = {
      slug: artifact.route.replace(/^\//, ''),
      titleTag: localized.titleTag ?? 'Traffic campaign | Traffic Creator',
      metaDescription: localized.metaDescription ?? '',
      keywords: localized.keywords ?? '',
      primaryKeyword: localized.primaryKeyword ?? localized.productName ?? 'Traffic campaign',
      secondaryKeyword: localized.secondaryKeyword ?? '',
      reviewCount: '0',
      ratingValue: '0',
      h1: localized.h1 ?? localized.productName ?? 'Traffic campaign',
      subheadline: localized.subheadline ?? '',
      problemH2: localized.problemH2 ?? '',
      problemText: localized.problemText ?? '',
      solutionPoints: localized.solutionPoints ?? [],
      featuresH2: localized.featuresH2 ?? '',
      features: localizedFeatures,
      testimonialsH2: localized.testimonialsH2 ?? '',
      testimonials: [],
      ctaH2: localized.ctaH2 ?? '',
      faqs: localized.faqs ?? [],
      relatedLinks: localized.relatedLinks ?? [],
      productName: localized.productName ?? localized.h1 ?? 'Traffic campaign',
      productDescription: localized.productDescription ?? localized.metaDescription ?? '',
      translations: localized.translations,
    };
    return (
      <TrafficLandingV2
        config={config}
        translations={localized.translations as TemplateTranslations}
      />
    );
  }

  return (
    <div className="tc-bespoke-release" data-landing-design="redesign-bespoke" lang={locale} dir={RTL_LOCALES.has(locale) ? 'rtl' : 'ltr'}>
      <a className="tc-bespoke-release__skip" href="#bespoke-main">{copy.skip}</a>
      <header className="tc-bespoke-release__header">
        <div className="tc-bespoke-release__header-inner">
          <Link className="tc-bespoke-release__brand" to="/" aria-label="Traffic Creator home">
            <span aria-hidden="true"><Bot size={21} /></span>
            <strong>TRAFFIC <em>CREATOR</em></strong>
          </Link>

          <nav className="tc-bespoke-release__nav" aria-label="Primary navigation">
            <Link to="/pricing">{copy.pricing}</Link>
            <Link to="/blog">{copy.resources}</Link>
            <Link to="/about">{copy.company}</Link>
          </nav>

          <div className="tc-bespoke-release__actions">
            <Link className="tc-bespoke-release__login" to="/login">{copy.login}</Link>
            <Link className="tc-bespoke-release__cta" to="/signup">{copy.start} <ArrowRight size={16} /></Link>
            <button
              type="button"
              className="tc-bespoke-release__menu-button"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              aria-controls="bespoke-mobile-navigation"
              onClick={() => setMenuOpen((current) => !current)}
            >
              {menuOpen ? <X size={21} /> : <Menu size={21} />}
            </button>
          </div>
        </div>

        <nav
          id="bespoke-mobile-navigation"
          className="tc-bespoke-release__mobile-nav"
          aria-label="Mobile navigation"
          hidden={!menuOpen}
        >
          <Link to="/pricing">{copy.pricing}</Link>
          <Link to="/blog">{copy.resources}</Link>
          <Link to="/about">{copy.company}</Link>
          <Link to="/login">{copy.login}</Link>
          <Link to="/signup">{copy.start}</Link>
        </nav>
      </header>

      <main id="bespoke-main" className="tc-bespoke-release__content">
        <NestedLandingFooterContext.Provider value={true}>{children}</NestedLandingFooterContext.Provider>
      </main>

      <LandingFooter />
    </div>
  );
};

export default BespokeLandingReleaseShell;
